import { useTranslation } from 'react-i18next';
import { AInputItem, ANumberInput, ARadioButtonGroupInputValue, ARadioButtons, ASwitch, ATextarea } from '../../../../common/inputs';
import { IFormValidator } from '../../../../core/FormValidator';
import { ResourceTextApplication } from '../../../../core/resources';
import { ProductionPlanQuestionType, QuestionResourceText } from '../../../../interfaces/enums';
import { IFarmQuestion } from '../../../../interfaces/IProductionPlan';
import { Typography } from '@mui/material';

interface IQuestionProps {
    question: IFarmQuestion;
    setQuestion: (question: IFarmQuestion) => void;
    validator: IFormValidator;
    readonly?: boolean;
    error?: boolean;
    required?: boolean;
}

export default function Question({ question, setQuestion, validator, readonly, required, error }: IQuestionProps) {
    const { t } = useTranslation<ResourceTextApplication[]>([
        'AnelmaLayout',
        'AnelmaCattleProductionPlan',
    ]);

    const booleanSwitch = () => {
        return (
            <ASwitch
                id={`question-${question.ResourceId}`}
                label={t(question.Description)}
                checked={question.Value === '1'}
                validator={validator}
                disabled={readonly}
                onChange={(checked) => {
                    checked
                        ? setQuestion({ ...question, Value: '1' })
                        : setQuestion({ ...question, Value: '2' });
                }}
                error={error}
            />
        );
    }

    const booleanSelect = () => {
        let items: AInputItem<ARadioButtonGroupInputValue>[] =
            question
                .Options?.sort((a, b) => b.SortOrder - a.SortOrder)
                .map(o => {
                    return { text: t(o.Description), value: o.Value };
                })
            ?? [];

        return (
            <>
                <Typography align='left' variant='body1'>
                    {t(question.Description)}
                </Typography>

                <ARadioButtons
                    id={`question-${question.ResourceId}`}
                    validator={validator}
                    value={question.Value ?? null}
                    items={items}
                    onChange={(value) => setQuestion({ ...question, Value: value?.toString() })}
                    disabled={readonly}
                />
            </>
        );
    }

    const multiselect = () => {
        // TODO
        return <> </>;
    }

    const text = () => {
        return (
            <>
                <ATextarea
                    id={`question-${question.ResourceId}`}
                    label={t(question.Description)}
                    validator={validator}
                    disabled={readonly}
                    value={question.Value || ''}
                    onChange={(value) => {
                        setQuestion({ ...question, Value: value});
                    }}
                    maxLength={400}
                    required={required}
                    error={error}
                />
            </>
        );
    }

    const singleSelect = () => {
        if (question.Options)
            return (
                <>
                    <div style={{ paddingLeft: 40 }}>
                        {question.Options
                            .sort((a, b) => a.SortOrder - b.SortOrder)
                            .map((qopt, i) => (
                            <ASwitch
                                id={`question-option-${question.ResourceId}-${i}`}
                                label={t(qopt.Description)}
                                checked={question.Value === qopt.Value}
                                validator={validator}
                                disabled={readonly}
                                onChange={(checked) => {
                                    if (checked)
                                        setQuestion({ ...question, Value: qopt.Value });
                                }}
                                required={required}
                                error={error}
                            />
                        ))}
                    </div>
                </>
            );
        else
            return <> </>;
    }

    const predefinedNumberSelect = () => {
        let items: AInputItem<ARadioButtonGroupInputValue>[] =
            question.Options?.map(o => { return { text: o.Value, value: o.Value }; }) ?? [];

        return (
            <>
                <Typography align='left' variant='body1'>
                    {t(question.Description)}
                </Typography>

                <ARadioButtons
                    id={`question-${question.ResourceId}`}
                    validator={validator}
                    value={question.Value ?? null}
                    items={items}
                    onChange={
                        (value) => question.Value === value?.toString()
                            ? setQuestion({ ...question, Value: null })
                            : setQuestion({ ...question, Value: value?.toString() })
                    }
                    disabled={readonly}
                />
            </>
        );
    }

    const numberInput = () => {
        return (
            <ANumberInput
                id={`question-${question.ResourceId}`}
                label={t(question.Description)}
                validator={validator}
                disabled={readonly}
                onChange={(value) => setQuestion({ ...question, Value: (value || 0).toString() })}
                value={question.Value ? Number.parseInt(question.Value) ?? 0 : 0}
                mode={'int'}
                min={0}
            />
        );
    }

    switch (question.Type) {
        case ProductionPlanQuestionType.Boolean:
            switch (question.ResourceId) {
                case QuestionResourceText.HasSalmonellaInsurance.toString():
                case QuestionResourceText.SoftSleepingPad.toString():
                    return booleanSelect();
                default:
                    return booleanSwitch();
            }
        case ProductionPlanQuestionType.MultiSelect:
            return multiselect();
        case ProductionPlanQuestionType.Text:
            return text();
        case ProductionPlanQuestionType.SingleSelect:
            return singleSelect();
        case ProductionPlanQuestionType.Number:
            switch (question.ResourceId) {
                case QuestionResourceText.ProductionEndYear.toString():
                case QuestionResourceText.PlanToIncreaseAnimalAmount.toString():
                    return predefinedNumberSelect();
                default:
                    return numberInput();
            }
        default:
            return <></>;
    };
}
